import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import {AuthUserType} from '@crema/types/models/AuthUser';
import jwtAxios, {setAuthToken} from './index';
import {useAppDispatch} from 'toolkit/hooks';
import {
  FETCH_START,
  FETCH_SUCCESS,
  SHOW_ERROR_MESSAGE,
  SHOW_MESSAGE,
} from '@crema/types/actions/Common.action';
import {SHOW_MODAL} from '@crema/constants/ActionTypes';
import {
  GET_CATEGORY_DATA,
  SET_BLOG_LISTING,
  SET_CAT_DATA,
  SET_PORTFOLIO_LISTING,
  SET_SINGLE_BLOG,
  SET_SINGLE_PORTFOLIO,
} from '@crema/types/actions/Dashboard.action';
import {getUserFromJwtAuth} from '@crema/helpers/AuthHelper';

interface JWTAuthContextProps {
  user: AuthUserType | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface SignInProps {
  email: string;
  password: string;
}

interface JWTAuthActionsProps {
  signInUser: (data: SignInProps) => void;
  logout: () => void;
}

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
});
const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signInUser: () => {},
  logout: () => {},
});

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

interface JWTAuthAuthProviderProps {
  children: ReactNode;
}

const JWTAuthAuthProvider: React.FC<JWTAuthAuthProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const [firebaseData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const getAuthUser = async () => {
      const token = localStorage.getItem('TokenAppcropsAdmin');

      if (!token) {
        setJWTAuthData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
        return;
      }
      setAuthToken(token);
      try {
        const res = await jwtAxios.get('/user/me');
        setJWTAuthData({
          user: res?.data,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        setJWTAuthData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    };

    getAuthUser();
  }, []);

  const signInUser = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    try {
      dispatch({type: FETCH_START});
      const {data} = await jwtAxios.post('auth/signin', {
        email,
        password,
      });
      localStorage.setItem('TokenAppcropsAdmin', data?.data);
      setAuthToken(data?.data);
      const res = await jwtAxios.get('user/me');
      setJWTAuthData({
        user: res?.data,
        isAuthenticated: true,
        isLoading: false,
      });
      dispatch({type: FETCH_SUCCESS});
      dispatch({type: SHOW_MESSAGE, payload: 'Successfully Login'});
    } catch (error) {
      console.log(error);
      setJWTAuthData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      dispatch({
        type: SHOW_ERROR_MESSAGE,
        payload: error.response.data.message,
      });
    }
  };

  const logout = async () => {
    localStorage.removeItem('TokenAppcropsAdmin');
    setAuthToken();
    setJWTAuthData({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
    dispatch({type: SHOW_MESSAGE, payload: 'Logged Out'});
    dispatch({type: GET_CATEGORY_DATA, payload: []});
    dispatch({type: SET_CAT_DATA, payload: null});
    dispatch({type: SHOW_MODAL, payload: false});
    dispatch({type: SET_PORTFOLIO_LISTING, payload: []});
    dispatch({type: SET_SINGLE_PORTFOLIO, payload: null});
    dispatch({type: SET_BLOG_LISTING, payload: []});
    dispatch({type: SET_SINGLE_BLOG, payload: null});
  };

  return (
    <JWTAuthContext.Provider
      value={{
        ...firebaseData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          signInUser,
          logout,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthAuthProvider;
